
import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowDown, Search, Users, LayoutDashboard, ListTodo } from 'lucide-react';

const HowItWorks = () => {
  return (
    <div className="flex flex-col min-h-screen">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-[#F8F9FF]">
        <div className="container mx-auto px-4 sm:px-8">
          <h1 className="text-4xl md:text-5xl font-bold text-center mb-8">How Discovery Hub Works</h1>
          <p className="text-xl text-gray-600 text-center max-w-3xl mx-auto mb-8">
            A comprehensive platform that streamlines lead generation, management, and conversion for B2B sales teams
          </p>
          <div className="flex justify-center">
            <Button variant="outline" size="lg" className="flex items-center gap-2">
              See How It Works
              <ArrowDown className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* Process Overview */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 sm:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Our 4-Step Process</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Discovery Hub transforms the way your sales team finds and converts B2B leads with our streamlined approach
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center mb-24">
            <div className="space-y-12">
              <div className="flex gap-6">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center font-bold">01</div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Discover Qualified Leads</h3>
                  <p className="text-gray-600">Use our powerful search engine to find businesses based on location, industry, size, and more. Filter results to match your ideal customer profile.</p>
                </div>
              </div>
              
              <div className="flex gap-6">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center font-bold">02</div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Enrich Lead Data</h3>
                  <p className="text-gray-600">Our system automatically enriches each lead with business details, contact information, social profiles, and engagement signals.</p>
                </div>
              </div>
              
              <div className="flex gap-6">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center font-bold">03</div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Prioritize & Manage</h3>
                  <p className="text-gray-600">Leads are automatically scored based on fit and intent signals. Organize and track them with our intuitive CRM interface.</p>
                </div>
              </div>
              
              <div className="flex gap-6">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center font-bold">04</div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Collaborate & Convert</h3>
                  <p className="text-gray-600">Assign leads, track activities, manage tasks, and collaborate with your team to convert prospects into customers.</p>
                </div>
              </div>
            </div>
            
            <div className="rounded-2xl overflow-hidden shadow-lg border border-blue-100">
              <img src="/lovable-uploads/a205c4d3-ecdc-4809-99ba-3a7777b13819.png" alt="Discovery Hub dashboard on laptop screen" className="w-full h-auto" />
            </div>
          </div>
        </div>
      </section>

      {/* Platform Features - Replaced with Feature Component */}
      <section className="py-16 md:py-24 bg-[#F8F9FF]">
        <div className="container mx-auto px-4 sm:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Platform Features</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">Explore the key components that make Discovery Hub the ultimate lead generation and management platform</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl border border-gray-100 shadow-sm">
              <div className="inline-flex w-12 h-12 items-center justify-center bg-blue-100 rounded-lg text-primary mb-6">
                <Search className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Location-Based Search</h3>
              <p className="text-gray-600">Find businesses based on geographic location with advanced search filters and radius targeting.</p>
            </div>
            
            <div className="bg-white p-8 rounded-xl border border-gray-100 shadow-sm">
              <div className="inline-flex w-12 h-12 items-center justify-center bg-blue-100 rounded-lg text-primary mb-6">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
                  <circle cx="11" cy="11" r="8"></circle>
                  <path d="m21 21-4.3-4.3"></path>
                  <path d="M11 8v6"></path>
                  <path d="M8 11h6"></path>
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-3">Custom Lead Scoring</h3>
              <p className="text-gray-600">Automatically score and prioritize leads based on fit, intent, and engagement signals.</p>
            </div>
            
            <div className="bg-white p-8 rounded-xl border border-gray-100 shadow-sm">
              <div className="inline-flex w-12 h-12 items-center justify-center bg-blue-100 rounded-lg text-primary mb-6">
                <ListTodo className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Lead Management</h3>
              <p className="text-gray-600">Organize your leads with custom categories, status tracking, notes, and task management.</p>
            </div>
            
            <div className="bg-white p-8 rounded-xl border border-gray-100 shadow-sm">
              <div className="inline-flex w-12 h-12 items-center justify-center bg-blue-100 rounded-lg text-primary mb-6">
                <Users className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Team Collaboration</h3>
              <p className="text-gray-600">Collaborate with your sales team with shared access, assignment features, and activity tracking.</p>
            </div>
            
            <div className="bg-white p-8 rounded-xl border border-gray-100 shadow-sm">
              <div className="inline-flex w-12 h-12 items-center justify-center bg-blue-100 rounded-lg text-primary mb-6">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
                  <path d="M12 2v4"></path>
                  <path d="M12 18v4"></path>
                  <path d="M4.93 4.93l2.83 2.83"></path>
                  <path d="M16.24 16.24l2.83 2.83"></path>
                  <path d="M2 12h4"></path>
                  <path d="M18 12h4"></path>
                  <path d="M4.93 19.07l2.83-2.83"></path>
                  <path d="M16.24 7.76l2.83-2.83"></path>
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-3">Automated Enrichment</h3>
              <p className="text-gray-600">Automatically gather business details, contact information, and social profiles for each lead.</p>
            </div>
            
            <div className="bg-white p-8 rounded-xl border border-gray-100 shadow-sm">
              <div className="inline-flex w-12 h-12 items-center justify-center bg-blue-100 rounded-lg text-primary mb-6">
                <LayoutDashboard className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Analytics Dashboard</h3>
              <p className="text-gray-600">Track lead conversion metrics, team performance, and sales pipeline analytics.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 sm:px-8 max-w-4xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Frequently Asked Questions</h2>
            <p className="text-gray-600">Everything you need to know about Discovery Hub</p>
          </div>

          <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
              <h3 className="text-xl font-semibold mb-2">How is Discovery Hub different from other lead generation tools?</h3>
              <p className="text-gray-600">Discovery Hub combines lead generation, data enrichment, scoring, and management in one platform with a focus on B2B sales. Unlike traditional tools, we provide complete lead management from discovery to conversion.</p>
            </div>

            <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
              <h3 className="text-xl font-semibold mb-2">How accurate is the lead data?</h3>
              <p className="text-gray-600">We use multiple data sources and verification methods to ensure high accuracy. Our enrichment process includes both automated and manual verification steps for critical information.</p>
            </div>

            <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
              <h3 className="text-xl font-semibold mb-2">Can I import my existing leads?</h3>
              <p className="text-gray-600">Yes, Discovery Hub supports CSV imports and can integrate with popular CRM platforms. You can easily bring your existing leads into the system.</p>
            </div>

            <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
              <h3 className="text-xl font-semibold mb-2">How does the lead scoring work?</h3>
              <p className="text-gray-600">Our proprietary scoring algorithm analyzes multiple factors including company demographics, engagement history, online presence, and buying intent signals to generate a comprehensive score.</p>
            </div>

            <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
              <h3 className="text-xl font-semibold mb-2">How many team members can use Discovery Hub?</h3>
              <p className="text-gray-600">Different subscription plans support different team sizes. Our plans scale from solo users to enterprise teams with unlimited members.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary text-white">
        <div className="container mx-auto px-4 sm:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Transform Your Lead Generation?</h2>
          <p className="text-white/80 max-w-2xl mx-auto mb-8">Join our beta program today and see Discovery Hub in action.</p>
          <Button size="lg" variant="outline" className="bg-white text-primary hover:bg-white/90" asChild>
            <Link to="/auth">Join Beta Now</Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default HowItWorks;
